import {createAction, props} from "@ngrx/store";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {
  CustomValueCompleteDto,
  FilterDto,
  IssueContentType,
  IssueListDto,
  IssueTemplateListDto,
  IssueType,
  MessageIssuePreviewDto,
  MissionIssuePreviewDto,
  ResourceProfileDto,
  TicketIssuePreviewDto
} from "@server-models";
import {Photo} from "@capacitor/camera";
import {FormGroup} from "@angular/forms";
import {TTemplatePreviewDto} from "@shared/components/issues/types/template-preview-dto.type";
import {TCurrentIssueDto} from "@shared/components/issues/types/issue-dto.type";
import {TSubmitIssueDto} from "@shared/components/issues/types/submit-issue-dto.type";
import {ILocalIssueDetailData} from "@shared/components/issues/interfaces/local-issue-detail-data.interface";
import {ISharedLocalBodyRequest} from "@shared/interfaces/shared-local-body-request.interface";

export const getIssueListPaginated = createAction(
  '[Issues-Base] Get Items Paginated Start',
  props<ISharedLocalBodyRequest>()
);

export const getIssueListPaginatedSuccess = createAction(
  '[Issues-Base] Get Items Paginated Success',
  props<{ data: ILocalPageOutputDto<IssueListDto[]> }>()
);

export const getIssueListPaginatedRefresh = createAction(
  '[Issues-Base] Get Items Paginated Refresh',
  props<{ data: ILocalPageOutputDto<IssueListDto[]> }>()
);

export const getIssueListPaginatedCancel = createAction(
  '[Issues-Base] Get Items Paginated Cancel'
);

export const getIssueListPaginatedFail = createAction(
  '[Issues-Base] Get Items Paginated Fail',
  props<{ error: Error }>()
);

export const getIssueTemplateList = createAction(
  '[Issues-Base] Get Items Template Start',
  props<{ templateId?: number; resourceId?: number; filters?: FilterDto[]; pageNumber?: number; }>()
);

export const getIssueTemplateListSuccess = createAction(
  '[Issues-Base] Get Items Template Success',
  props<{
    data: ILocalPageOutputDto<IssueTemplateListDto[]>;
    templateId?: number;
    resourceId?: number;
  }>()
);

export const getIssueTemplateListDone = createAction(
  '[Issues-Base] Get Items Template Done'
);

export const getIssueTemplateListFail = createAction(
  '[Issues-Base] Get Issue Template List Fail',
  props<{ error: Error }>()
);

export const getPreview = createAction(
  '[Issues-Base] Get Preview Start',
  props<{ templateId: string }>()
);

export const getIssuePreviewByIdMessage = createAction(
  '[Issue-Base Message] Get Issue Preview By Id',
  props<{ templateId: number; issueType: IssueContentType; resourceId?: number }>()
);

export const getIssuePreviewByIdMission = createAction(
  '[Issue-Base Mission] Get Issue Preview By Id',
  props<{ templateId: number; issueType: IssueContentType; resourceId?: number }>()
);

export const getIssuePreviewByIdTicket = createAction(
  '[Issue-Base Ticket] Get Issue Preview By Id',
  props<{ templateId: number; issueType: IssueContentType; resourceId?: number }>()
);

export type TIssuePreview = {
  templateId: number;
  issueType: IssueContentType;
  resourceId?: number;
};

export const getIssuePreview = createAction(
  '[Issues-Base] Get Issue Preview',
  props<{ templateId: number; contentType: IssueContentType; resourceId?: number }>()
);

export const getIssuePreviewSuccess = createAction(
  '[Issues-Base] Get Issue Preview Success',
  props<{ data: any; isRefresh?: boolean; templateId: number; resourceId?: number; contentType: IssueContentType; }>()
);

export const getIssuePreviewDone = createAction(
  '[Issues-Base] Get Issue Preview Done',
);

export const getIssuePreviewFail = createAction(
  '[Issues-Base] Get Issue Preview Fail',
  props<{ error: Error }>()
);

export const getById = createAction(
  '[Issues-Base] Get By Id Start',
  props<{ issueId: number, issueType: IssueType }>()
);

export const getByIdMessage = createAction(
  '[Issue-Base Message] Get By Id Message Start',
  props<{ issueId: number, issueType: IssueType }>()
);

export const getByIdMission = createAction(
  '[Issue-Base Mission] Get By Id Mission Start',
  props<{ issueId: number, issueType: IssueType }>()
);

export const getByIdTicket = createAction(
  '[Issue-Base Ticket] Get By Id Ticket Start',
  props<{ issueId: number, issueType: IssueType }>()
);

export const getByIdControlMessage = createAction(
  '[Issue-Base Control Message] Get By Id Control Message Start',
  props<{ issueId: number, issueType: IssueType }>()
);

export type TGetIssue =
  | ReturnType<typeof getByIdMessage>
  | ReturnType<typeof getByIdMission>
  | ReturnType<typeof getByIdTicket>
  | ReturnType<typeof getByIdControlMessage>;

export const getIssueByIssueType = createAction(
  '[Issues-Base] Get Issue By Issue Type',
  props<{ issueId: number, issueType: IssueType }>()
);

export const getByIdSuccess = createAction(
  '[Issues-Base] Get By Id Success',
  props<{ data: TCurrentIssueDto, cacheGuid: string }>()
);

export const getByIdFail = createAction(
  '[Issues-Base] Get By Id Fail',
  props<{ error: Error }>()
);

export const prepareFormsToSendTemplatePreview = createAction(
  '[Issues-Base] Prepare Forms Start',
  props<{ form: FormGroup, templatePreviewDto: TTemplatePreviewDto }>()
);

export const prepareFormsToSendTemplatePreviewDone = createAction(
  '[Issues-Base] Prepare Forms Done',
  props<{ submitReady: TSubmitIssueDto }>()
);

export const sendTemplatePreview = createAction(
  '[Issues-Base] Send Template Preview Start',
  props<{ issuePrepared: TSubmitIssueDto }>()
);

export const sendTemplatePreviewMessage = createAction(
  '[Issues-Base] Send Template Preview Message Start',
  props<{
    templateId: number,
    issueType: IssueContentType,
    customPropertyValues: CustomValueCompleteDto[],
    templatePreviewDto: MessageIssuePreviewDto,
    resourceDto?: ResourceProfileDto
  }>()
);

export const sendTemplatePreviewMission = createAction(
  '[Issues-Base] Send Template Preview Mission Start',
  props<{
    templateId: number,
    issueType: IssueContentType,
    customPropertyValues: CustomValueCompleteDto[],
    templatePreviewDto: MissionIssuePreviewDto,
    resourceDto?: ResourceProfileDto
  }>()
);

export const sendTemplatePreviewTicket = createAction(
  '[Issues-Base] Send Template Preview Ticket Start',
  props<{
    templateId: number,
    issueType: IssueContentType,
    customPropertyValues: CustomValueCompleteDto[],
    templatePreviewDto: TicketIssuePreviewDto,
    resourceDto?: ResourceProfileDto
  }>()
);

export type TsubmitIssuePreviewByIdByIssue =
  | ReturnType<typeof sendTemplatePreviewMessage>
  | ReturnType<typeof sendTemplatePreviewMission>
  | ReturnType<typeof sendTemplatePreviewTicket>;

export const sendTemplatePreviewSuccess = createAction(
  '[Issues-Base] Send Template Preview Success',
  props<any>()
);

export const sendTemplatePreviewFail = createAction(
  '[Issues-Base] Send Template Preview Fail',
  props<{ error: Error }>()
);

export const formatReadDetailData = createAction(
  '[Issues-Base] Format read detail data Start'
);

export const formatReadDetailDataFinish = createAction(
  '[Issues-Base] Format read detail data Finish',
  props<{ detailDataFormatted: ILocalIssueDetailData }>()
);

export const askPictureInput = createAction(
  '[Issues-Base] Ask Picture Input',
  props<{ isLoadingId: number | string, pictureId: number }>()
);

export const askPictureInputSuccess = createAction(
  '[Issues-Base] Ask Picture Input Success',
  props<{ picture: Photo, pictureId: number }>()
);

export const askPictureInputFail = createAction(
  '[Issues-Base] Ask Picture Input Fail',
  props<{ error: Error }>()
);

export const reducePicture = createAction(
  '[Issues-Base] Reduce Picture size',
  props<{ picture: Photo, pictureId: number }>()
);

export const reducePictureSuccess = createAction(
  '[Issues-Base] Reduce Picture size Success',
  props<{ blob: Blob, pictureId: number }>()
);

export const reducePictureError = createAction(
  '[Issues-Base] Reduce Picture size Error',
  props<{ error: Error }>()
);

export const savePicture = createAction(
  '[Issues-Base] Save Picture to tenant',
  props<{ blob: Blob, fileType: string, pictureId: number }>()
);

export const savePictureSuccess = createAction(
  '[Issues-Base] Save Picture to tenant Success',
  props<{ pictureId: number, fileId: number, path: string }>()
);

export const savePictureFail = createAction(
  '[Issues-Base] Save Picture to tenant Fail',
  props<{ error: Error }>()
);

export const savedPictureClear = createAction(
  '[Issues-Base] Saved Picture Clear',
  props<any>()
);

export const convertDateURLToBlob = createAction(
  '[Issues-Base] Convert dateUrl to blob',
  props<{ isLoadingId: number | string, dataUrl: string }>()
);

export const convertDateURLToBlobSuccess = createAction(
  '[Issues-Base] Convert dateUrl to blob Success',
  props<{ blob: Blob; dataUrl: string; index: number | string }>()
);

export const convertDateURLToBlobFail = createAction(
  '[Issues-Base] Convert dateUrl to blob Fail',
  props<{ error: Error }>()
);

export const saveSignature = createAction(
  '[Issues-Base] Save signature',
  props<{ blob: Blob; dataUrl: string; index: number | string }>()
);

export const saveSignatureSuccess = createAction(
  '[Issues-Base] Save signature Success',
  props<{ id: number; dataUrl: string; index: number | string }>()
);

export const saveSignatureFail = createAction(
  '[Issues-Base] Save signature Fail',
  props<{ error: Error }>()
);

export const savedSignatureClear = createAction(
  '[Issues-Base] Selected Signature Clear',
  props<any>()
);
